import React from 'react';
import { FormattedMessage } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { messages } from './messages';
import type { ExtensionEnvironment } from 'view/common/forge-ui/types';

export interface Props {
    extensionName: string;
    extensionEnvType: ExtensionEnvironment;
    extensionEnvKey?: string;
}

export const PRODUCTION_ENVIRONMENT = 'PRODUCTION';
export const STAGING_ENVIRONMENT = 'STAGING';
export const DEVELOPMENT_ENVIRONMENT = 'DEVELOPMENT';
export const DEFAULT_ENVIRONMENT_KEY = 'default';

export const ExtensionTitle = ({ extensionEnvType, extensionEnvKey, extensionName }: Props) => {
    di(EnvironmentLozenge);

    if (!shouldShowEnv(extensionEnvType)) {
        return <>{extensionName}</>;
    }

    return (
        <Box xcss={wrapperStyles} as="span">
            <>{nameWithCustomEnvironment(extensionName, extensionEnvType, extensionEnvKey)} </>
            <EnvironmentLozenge environmentType={extensionEnvType} />
        </Box>
    );
};

export const EnvironmentLozenge = (props: { environmentType: ExtensionEnvironment }) => {
    const { environmentType } = props;
    const labelKey = getEnvironmentTranslationKey(environmentType);

    return labelKey ? (
        <Lozenge appearance={environmentType === DEVELOPMENT_ENVIRONMENT ? 'default' : 'moved'}>
            <FormattedMessage {...labelKey} />
        </Lozenge>
    ) : null;
};

const shouldShowEnv = (extensionEnvType: ExtensionEnvironment) =>
    [STAGING_ENVIRONMENT, DEVELOPMENT_ENVIRONMENT].includes(extensionEnvType);

const getEnvironmentTranslationKey = (environmentType: ExtensionEnvironment) => {
    switch (environmentType) {
        case DEVELOPMENT_ENVIRONMENT:
            return messages.developmentEnvironmentLozenge;
        case STAGING_ENVIRONMENT:
            return messages.stagingEnvironmentLozenge;
        default:
            return null;
    }
};

const nameWithCustomEnvironment = (name: string, environmentType: ExtensionEnvironment, environmentKey?: string) => {
    if (environmentType === DEVELOPMENT_ENVIRONMENT && environmentKey && environmentKey !== DEFAULT_ENVIRONMENT_KEY) {
        return `${name} - ${environmentKey}`;
    }
    return name;
};

const wrapperStyles = xcss({
    alignItems: 'baseline',
});
